import { SUBSCRIPTION_PLAN_ENDPOINTS } from "../constants/billingEndpoints";
import { buildPaginationRequest, getTotalPages } from "../utils/pagination";
import {
  buildCollectionResult,
  assertSuccessfulResponse,
  extractEntity,
} from "../utils/response";

const BILLING_DEBUG = process.env.NODE_ENV !== "production";

const normalizeSubscriptionPlan = (plan = {}) => ({
  ...plan,
  isActive:
    typeof plan?.isActive === "boolean" ? plan.isActive : Boolean(plan?.active),
  active:
    typeof plan?.active === "boolean" ? plan.active : Boolean(plan?.isActive),
});

export const createSubscriptionPlansService = (apiClient) => ({
  async list({ page = 1, pageSize = 10 } = {}) {
    const response = await apiClient.post(
      SUBSCRIPTION_PLAN_ENDPOINTS.list,
      buildPaginationRequest({ page, pageSize, orderBy: "id" })
    );

    if (BILLING_DEBUG) {
      console.info("[billing:Subscription plan] raw list payload keys", Object.keys(response?.data || {}));
    }

    return buildCollectionResult({
      payload: response.data || {},
      preferredKeys: ["ahdSubscriptionPlans", "subscriptionPlans", "plans"],
      pageSize,
      normalizeItem: normalizeSubscriptionPlan,
      fallbackMessage: "Unable to load subscription plans right now.",
      getTotalPages,
    });
  },

  async listByService(serviceId, { page = 1, pageSize = 10 } = {}) {
    const response = await apiClient.post(
      SUBSCRIPTION_PLAN_ENDPOINTS.listByService,
      buildPaginationRequest({
        page,
        pageSize,
        orderBy: "ASC",
        extra: {
          service_id: Number(serviceId),
        },
      })
    );

    if (BILLING_DEBUG) {
      console.info("[billing:Subscription plan] raw service payload keys", Object.keys(response?.data || {}));
    }

    return buildCollectionResult({
      payload: response.data || {},
      preferredKeys: ["ahdSubscriptionPlans", "subscriptionPlans", "plans"],
      pageSize,
      normalizeItem: normalizeSubscriptionPlan,
      fallbackMessage: "Unable to load subscription plans right now.",
      getTotalPages,
    });
  },

  async create(planPayload) {
    const response = await apiClient.post(
      SUBSCRIPTION_PLAN_ENDPOINTS.create,
      planPayload
    );
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to create subscription plan."
    );

    return normalizeSubscriptionPlan(
      extractEntity(payload, ["subscriptionPlan", "plan"])
    );
  },

  async update(planPayload) {
    const response = await apiClient.put(
      SUBSCRIPTION_PLAN_ENDPOINTS.update,
      planPayload
    );
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to update subscription plan."
    );

    return normalizeSubscriptionPlan(
      extractEntity(payload, ["subscriptionPlan", "plan"])
    );
  },

  async getById(id) {
    const response = await apiClient.get(SUBSCRIPTION_PLAN_ENDPOINTS.details(id));
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to load subscription plan."
    );

    return normalizeSubscriptionPlan(
      extractEntity(payload, ["subscriptionPlan", "plan"])
    );
  },

  async remove(id) {
    await apiClient.delete(SUBSCRIPTION_PLAN_ENDPOINTS.remove(id));
    return true;
  },

  async toggleStatus(id, isActive) {
    const response = await apiClient.patch(
      SUBSCRIPTION_PLAN_ENDPOINTS.toggleStatus(id, isActive),
      null
    );
    const payload = assertSuccessfulResponse(
      response.data,
      "Unable to update subscription plan status."
    );

    return normalizeSubscriptionPlan(
      extractEntity(payload, ["subscriptionPlan", "plan"])
    );
  },
});
